import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Settings,
  FileText,
  UserCog,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Mail,
  LogOut,
  BarChart3,
  Users,
  Timer,
} from 'lucide-react';

import { cn, showToastMessage, useRoleIds } from '@/utils';
import { NavItem, NavItemComponentProps, UserRole } from '@/types';

import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { logout as clearAuth, RootState } from '@/store';
import { clearUser } from '@/store/slices/userSlice';
import apiSlice from '@/store/api/apiSlice';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { LuHandCoins } from 'react-icons/lu';
const isMobile = window.innerWidth < 768;
interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

function useFlyoutPosition() {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, []);

  const openFlyout = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({ top: rect.top, left: rect.right + 8 });
    }
    setShow(true);
  };

  const scheduleClose = () => {
    hideTimeout.current = setTimeout(() => setShow(false), 150);
  };

  const cancelClose = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
  };

  const close = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setShow(false);
  };

  return { triggerRef, show, position, openFlyout, scheduleClose, cancelClose, close };
}

function FlyoutChild({
  item,
  level = 0,
  onNavigate,
}: {
  item: NavItem;
  level?: number;
  onNavigate?: () => void;
}) {
  const hasChildren = item.children && item.children.length > 0;

  if (hasChildren) {
    return (
      <div>
        <div
          className="whitespace-nowrap px-3 py-1.5 text-xs font-semibold text-sidebar-text/80"
          style={{ paddingLeft: `${12 + level * 12}px` }}
        >
          {item.name}
        </div>
        {item.children!.map((child) => (
          <FlyoutChild key={child.name} item={child} level={level + 1} onNavigate={onNavigate} />
        ))}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href!}
      onClick={onNavigate}
      style={{ paddingLeft: `${12 + level * 12}px` }}
      className={({ isActive }) =>
        cn(
          'block whitespace-nowrap px-3 py-1.5 text-xs sm:text-sm transition-colors',
          isActive ? 'bg-golden-gredient text-white' : 'text-sidebar-text hover:bg-green/40'
        )
      }
    >
      {item.name}
    </NavLink>
  );
}

function FlyoutPanel({
  item,
  position,
  onMouseEnter,
  onMouseLeave,
  onNavigate,
}: {
  item: NavItem;
  position: { top: number; left: number };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onNavigate: () => void;
}) {
  const hasChildren = item.children && item.children.length > 0;

  return createPortal(
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ top: position.top, left: position.left }}
      className="fixed z-50 min-w-[190px] max-w-xs rounded-lg border border-green3 bg-sidebar py-1.5 shadow-xl"
    >
      {hasChildren ? (
        <>
          <div className="whitespace-nowrap border-b border-b-green3 px-3 py-2 text-sm font-semibold text-sidebar-text">
            {item.name}
          </div>
          <div className="py-1">
            {item.children!.map((child) => (
              <FlyoutChild key={child.name} item={child} onNavigate={onNavigate} />
            ))}
          </div>
        </>
      ) : (
        <NavLink
          to={item.href!}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'block whitespace-nowrap px-3 py-2 text-xs sm:text-sm transition-colors',
              isActive ? 'bg-golden-gredient text-white' : 'text-sidebar-text hover:bg-green/40'
            )
          }
        >
          {item.name}
        </NavLink>
      )}
    </div>,
    document.body
  );
}

function NavItemComponent({
  item,
  isOpen,
  level = 0,
  activeMenu,
  setActiveMenu,
  onToggle,
}: NavItemComponentProps) {
  const isExpanded = activeMenu === item.name;
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  useEffect(() => {
    if (hasActiveChild && isOpen) {
      setActiveMenu(item.name);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setActiveMenu(isExpanded ? null : item.name);
    }
  };
  const Icon = item?.icon;
  const indent = level * 16;
  const { triggerRef, show, position, openFlyout, scheduleClose, cancelClose, close } =
    useFlyoutPosition();

  if (hasChildren) {
    return (
      <div>
        <div
          ref={triggerRef}
          onMouseEnter={!isOpen ? openFlyout : undefined}
          onMouseLeave={!isOpen ? scheduleClose : undefined}
        >
          <button
            onClick={handleClick}
            className={cn(
              'flex w-full items-center rounded-lg px-3 py-2 text-base font-medium transition-colors',
              isActive || hasActiveChild
                ? 'bg-golden-gredient text-white'
                : 'text-sidebar-text hover:bg-green/40',
              !isOpen && 'justify-center !px-0'
            )}
            style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          >
            {item.img
              ? item.img && (
                  <img src={item.img} className={cn('h-5 w-5 shrink-0', isOpen && 'mr-3')} />
                )
              : Icon && <Icon className={cn('h-5 w-5 shrink-0', isOpen && 'mr-3')} />}

            {isOpen && (
              <>
                <span className="flex-1 text-left text-xs sm:text-sm">{item.name}</span>
                {isExpanded ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </>
            )}
          </button>
        </div>

        {!isOpen && show && (
          <FlyoutPanel
            item={item}
            position={position}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onNavigate={() => {
              close();
              if (isMobile) onToggle?.();
            }}
          />
        )}

        {/* Tree Menu */}
        {isExpanded && isOpen && (
          <div className=" ml-6">
            <div className="relative border-l-2 border-l-green  space-y-4 py-2">
              {item.children!.map((child) => (
                <div key={child.name} className="relative">
                  {/* tick */}
                  <span className="absolute -left-0.5 top-2 h-0.5 w-4 bg-gray-600"></span>

                  {child.children ? (
                    <NavItemComponent
                      item={child}
                      isOpen={isOpen}
                      level={level + 1}
                      activeMenu={activeMenu}
                      setActiveMenu={setActiveMenu}
                      onToggle={onToggle}
                    />
                  ) : (
                    <NavLink
                      onClick={() => {
                        if (isMobile) {
                          onToggle?.();
                        }
                      }}
                      to={child.href!}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center text-xs sm:text-sm transition-colors',
                          isActive
                            ? 'text-sidebar-text font-medium'
                            : 'text-sidebar-text hover:text-green3'
                        )
                      }
                    >
                      - {child.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={triggerRef}
      onMouseEnter={!isOpen ? openFlyout : undefined}
      onMouseLeave={!isOpen ? scheduleClose : undefined}
    >
      <NavLink
        to={item.href!}
        onClick={() => {
          if (isMobile) {
            onToggle?.();
          }
        }}
        className={({ isActive }) =>
          cn(
            'flex items-center rounded-lg px-3 py-1.5 sm:py-3 text-sm font-medium transition-colors',
            isActive
              ? 'bg-golden-gredient text-white border-l-2'
              : 'text-sidebar-text hover:bg-green/40',
            !isOpen && 'justify-center !px-0'
          )
        }
        style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      >
        {item.img
          ? item.img && (
              <img src={item.img} className={cn('h-5 w-5 brightness-200', isOpen && 'mr-3')} />
            )
          : Icon && <Icon className={cn('h-5 w-5 text-sidebar-text', isOpen && 'mr-3')} />}

        {isOpen && <span className="text-xs sm:text-sm">{item.name}</span>}
      </NavLink>

      {!isOpen && show && (
        <FlyoutPanel
          item={item}
          position={position}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          onNavigate={() => {
            close();
            if (isMobile) onToggle?.();
          }}
        />
      )}
    </div>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const imagekit = import.meta.env.VITE_IMAGEKIT_URL;
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();

  const { isOwner } = useRoleCheck();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const { superAdminID, adminID, superMasterId, masterId, userId } = useRoleIds();
  const userRole = user?.data?.role?.name as UserRole | undefined;
  const DOWNLINE_ROLE_VISIBILITY: Record<UserRole, UserRole[]> = {
    OWNER: ['SUPER MASTER', 'MASTER', 'USER'],
    'SUPER ADMIN': ['ADMIN', 'USER'],
    ADMIN: ['SUPER MASTER', 'MASTER', 'USER'],
    'SUPER MASTER': ['MASTER', 'USER'],
    MASTER: ['USER'],
    USER: [],
  };

  const visibleRoles: UserRole[] =
    userRole && userRole in DOWNLINE_ROLE_VISIBILITY ? DOWNLINE_ROLE_VISIBILITY[userRole] : [];

  const downlineChildren: NavItem[] = [
    // { name: 'Super Admin', href: `/members/${superAdminID}`, role: 'SUPER ADMIN' },
    // { name: 'Admin', href: `/members/${adminID}`, role: 'ADMIN' },
    // { name: 'Sub Admin', href: `/members/${subAdminId}`, role: 'SUB ADMIN' },
    { name: 'Super Master', href: `/members/${superMasterId}`, role: 'SUPER MASTER' },
    { name: 'Master', href: `/members/${masterId}`, role: 'MASTER' },
    { name: 'User', href: `/members/${userId}`, role: 'USER' },
    { name: 'Settlement', href: `/settlement`, role: 'USER' },
  ].filter((item) => visibleRoles.includes(item.role as UserRole));

  const navigation: NavItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      img: `${imagekit}/sidebar/dashboard.svg`,
    },
    {
      name: 'Downline',
      icon: BarChart3,
      img: `${imagekit}/sidebar/downline.svg`,
      children: downlineChildren,
    },
    {
      name: 'Risk Management',
      href: '/risk',
      icon: Users,
      img: `${imagekit}/sidebar/risk.svg`,
    },
    {
      name: 'My Reports',
      icon: FileText,
      img: `${imagekit}/sidebar/myreport.svg`,
      children: [
        { name: 'Bet List', href: '/bet-list' },
        { name: 'Casino Bet List', href: '/casino-bet-list' },
        { name: 'P/L Report By Downline', href: '/pl-by-downline' },
        { name: 'P/L Report By Event', href: '/pl-by-event' },
        { name: 'P/L Report By Player', href: '/pl-by-player' },
        { name: 'P/L Report By Casino Downline', href: '/casinopl-by-downline' },
        { name: 'P/L Report By Casino Players', href: '/casinopl-by-player' },
        { name: 'Business Report', href: '/business-reports' },
        { name: 'Inactive Members', href: '/inactive-members' },
      ],
    },

    {
      name: 'Event Management',
      img: `${imagekit}/sidebar/event.svg`,
      icon: CreditCard,
      onlyOwner: true,
      children: [
        { name: 'Events', href: '/event-management' },
        { name: 'Bet Config', href: '/bet-setting' },
      ],
    },

    {
      name: 'Casino Control',
      icon: Mail,
      href: '/casino-management',
      onlyOwner: true,
      img: `${imagekit}/sidebar/casino.svg`,
    },

    {
      name: 'Content Management',
      href: '/content-management',
      img: `${imagekit}/sidebar/content.svg`,
      icon: UserCog,
      onlyOwner: true,
    },

    {
      name: 'Bonus Management',
      icon: Settings,
      img: `${imagekit}/sidebar/bonus.svg`,
      children: [
        {
          name: 'Policy Management',
          href: '/bonus/policy-management',
        },
        {
          name: 'Bonus Statement',
          href: '/bonus/statement',
        },
      ],
      onlyOwner: true,
    },
    {
      name: 'Banking Management',
      icon: Settings,
      img: `${imagekit}/sidebar/bonus.svg`,
      children: [
        {
          name: 'Deposit/Withdraw Request',
          href: '/requests',
        },
        {
          name: 'Manage Accounts',
          href: '/manage-account',
        },
        // {
        //   name: 'Crypto Conversion Settings',
        //   href: '/crypto-conversion',
        // },
      ],
      onlyOwner: true,
    },
    {
      name: 'PG Management',
      icon: LuHandCoins,
      href: '/pg-management',
      onlyOwner: true,
    },
    {
      name: 'Account Statement',
      icon: Settings,
      href: '/rolewise-account-statement',
    },
    // {
    //   name: 'Account Statement',
    //   icon: Settings,
    //   href: '/account-statement',
    //   img: `${imagekit}/sidebar/account.svg`,
    //   onlyBanker: true,
    // },
    {
      name: 'Export Report History',
      icon: Settings,
      href: '/export',
      img: `${imagekit}/sidebar/export.svg`,
    },
    {
      name: 'Change Password',
      icon: Settings,
      img: `${imagekit}/sidebar/password.svg`,
      href: '/settings',
    },
    {
      name: 'Activity Log',
      img: `${imagekit}/sidebar/activity.svg`,
      icon: Settings,
      href: '/activity-logs',
    },
    {
      name: 'Agent Session Settings',
      icon: Timer,
      href: '/agent-session-settings',
      onlyOwner: true,
    },
  ];
  const filteredNavigation = React.useMemo(() => {
    // const role = user?.data?.role?.name;

    if (isOwner) {
      return navigation.filter((item) => !item.onlyBanker);
    }

    return navigation.filter((item) => {
      if (item.onlyOwner) return false;
      if (item.onlyBanker) return false;
      return true;
    });
  }, [navigation, user]);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      apiSlice.util.resetApiState();
      showToastMessage('success', 'You have been logged out successfully.');
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-20 bg-sidebar/20   lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col text-sidebar-text bg-sidebar transition-all duration-300 overflow-hidden  border-r border-r-green3',
          isOpen ? 'w-72 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-16 items-center bg-black px-4">
          <img src={`${imagekit}/login/logo.png`} alt="" className="max-h-full" />

          {/* Toggle button - desktop only */}
          {/* <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'hidden lg:flex h-6 w-6',
              isOpen ? 'ml-auto' : 'fixed left-2 top-3 z-40 '
            )}
          >
            <reactIcons.squareBack
              className={`w-6 h-6 bg-white text-black p-0.5 rounded-sm transition-all duration-300 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </Button> */}
        </div>

        {/* Input */}
        {/* <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search..." className="bg-transparent pl-9 text-sidebar-text border-green " />
        </div> */}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto py-1.5  sm:py-3 px-1.5 sm:px-4">
          {filteredNavigation?.map((item) => (
            <NavItemComponent
              key={item.name}
              item={item}
              isOpen={isOpen}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onToggle={onToggle}
            />
          ))}
        </nav>

        {/* Logout */}

        <div
          onClick={handleLogout}
          className={`${isOpen ? 'flex-start': 'flex items-center justify-center'}  gap-3 cursor-pointer text-xs sm:text-sm font-medium text-sidebar-text border-t border-t-green3 py-2 sm:py-5 px-2`}
        >
          <LogOut className="h-5 w-5" />
      {isOpen && 'Logout'}    
        </div>
      </div>
    </>
  );
}
